import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

/**
 * Get image metrics for a specific shop
 * @param {string} shopId - Shop ID
 * @returns {Promise<Object>} Image metrics for the shop
 */
export const getShopImageMetrics = async (shopId) => {
  try {
    // Get the shop's metrics document
    const shopDoc = await getDoc(doc(db, 'shops', shopId));
    
    if (!shopDoc.exists()) {
      console.log('No metrics found for shop');
      return {
        totalStats: {
          imagesProcessed: 0,
          storageSaved: 0,
          compressionRatio: 100
        }
      };
    }

    const metrics = shopDoc.data().metrics || {};
    
    // Get the values from the actual structure
    const totalImagesProcessed = parseInt(metrics.totalImagesProcessed || 0);
    const totalBytesOriginalMB = parseFloat(metrics.totalBytesOriginalMB || 0);
    const totalBytesProcessedMB = parseFloat(metrics.totalBytesProcessedMB || 0);
    const totalBytesSavedMB = parseFloat(metrics.totalBytesSavedMB || 0);
    const averageCompressionRatio = parseFloat(metrics.averageCompressionRatio || 100);
    const lastImageUpdate = metrics.lastImageUpdate;

    // Process monthly data if available
    let monthlyData = [];
    if (metrics.monthlyImageStats) {
      Object.entries(metrics.monthlyImageStats).forEach(([month, data]) => {
        monthlyData.push({
          month,
          imagesProcessed: parseInt(data.imagesProcessed || 0),
          storageSaved: parseFloat(data.bytesSavedMB || 0),
          compressionRatio: parseFloat(data.compressionRatio || 100)
        });
      });
    }
    
    // Log the actual monthly data from Firebase for debugging
    console.log('Monthly Image Stats from Firebase:', metrics.monthlyImageStats);
    
    // Process daily data if available
    let dailyData = [];
    if (metrics.dailyImageStats) {
      Object.entries(metrics.dailyImageStats).forEach(([date, data]) => {
        dailyData.push({
          date,
          imagesProcessed: parseInt(data.imagesProcessed || 0),
          storageSaved: parseFloat(data.bytesSavedMB || 0),
          compressionRatio: parseFloat(data.compressionRatio || 100)
        });
      });
    }
    
    // Log the actual daily data from Firebase for debugging
    console.log('Daily Image Stats from Firebase:', metrics.dailyImageStats);
    
    // Sort data chronologically
    monthlyData.sort((a, b) => a.month.localeCompare(b.month));
    dailyData.sort((a, b) => a.date.localeCompare(b.date));
    
    return {
      totalStats: {
        imagesProcessed: totalImagesProcessed,
        storageSaved: totalBytesSavedMB * 1024 * 1024, // Convert MB to bytes
        compressionRatio: Number(averageCompressionRatio), // Ensure it's a number
        lastUpdate: lastImageUpdate
      },
      monthlyData,
      dailyData
    };
  } catch (error) {
    console.error('Error fetching shop image metrics:', error);
    throw error;
  }
};

/**
 * Get aggregated image metrics for all shops belonging to a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Aggregated metrics for all shops
 */
export const getAllShopsMetrics = async (userId) => {
  try {
    // Query all shops belonging to the user
    const shopsRef = collection(db, 'shops');
    const shopsQuery = query(shopsRef, where('userId', '==', userId));
    const shopsSnapshot = await getDocs(shopsQuery);
    
    if (shopsSnapshot.empty) {
      console.log('No shops found for user');
      return {
        totalStats: {
          imagesProcessed: 0,
          storageSaved: 0,
          compressionRatio: 0
        },
        shopBreakdown: [],
        templateUsage: {
          total: 0,
          successful: 0,
          failed: 0,
          byTemplate: []
        }
      };
    }

    // Initialize aggregated metrics
    let totalImagesProcessed = 0;
    let totalBytesSavedMB = 0;
    let totalTemplateUsage = 0;
    let successfulTemplateUsage = 0;
    let failedTemplateUsage = 0;
    let totalCreditsUsed = 0;
    let totalSizechartsGenerated = 0;
    const templateUsageByTemplate = {};
    const shopBreakdown = [];
    
    // Process each shop
    shopsSnapshot.forEach(shopDoc => {
      const shopData = shopDoc.data();
      const metrics = shopData.metrics || {};
      
      // Image metrics (now considered as product pushes)
      const shopImagesProcessed = parseInt(metrics.totalImagesProcessed || 0);
      const shopBytesSavedMB = parseFloat(metrics.totalBytesSavedMB || 0);
      const shopSizechartsGenerated = parseInt(metrics.sizechartsGenerated || 0);
      
      totalImagesProcessed += shopImagesProcessed;
      totalBytesSavedMB += shopBytesSavedMB;
      totalSizechartsGenerated += shopSizechartsGenerated;
      
      // Credit usage metrics
      const shopCreditsUsed = parseInt(metrics.creditUsage?.total || 0);      
      totalCreditsUsed += shopCreditsUsed;
      
      // Template usage metrics
      const shopTemplateUsage = parseInt(metrics.templateUsage?.total || 0);
      const shopSuccessfulTemplateUsage = parseInt(metrics.templateUsage?.successful || 0);
      const shopFailedTemplateUsage = parseInt(metrics.templateUsage?.failed || 0);
      
      totalTemplateUsage += shopTemplateUsage;
      successfulTemplateUsage += shopSuccessfulTemplateUsage;
      failedTemplateUsage += shopFailedTemplateUsage;
      
      // Process template usage by template
      const byTemplate = metrics.templateUsage?.byTemplate || {};
      Object.entries(byTemplate).forEach(([templateId, usage]) => {
        if (!templateUsageByTemplate[templateId]) {
          templateUsageByTemplate[templateId] = {
            templateId,
            count: 0,
            successful: 0,
            failed: 0
          };
        }
        
        templateUsageByTemplate[templateId].count += parseInt(usage.total || 0);
        templateUsageByTemplate[templateId].successful += parseInt(usage.successful || 0);
        templateUsageByTemplate[templateId].failed += parseInt(usage.failed || 0);
      });
      
      // Add to shop breakdown
      shopBreakdown.push({
        id: shopDoc.id,
        name: shopData.name || shopData.shopName || 'Unknown Shop',
        domain: shopData.domain || shopData.shopDomain || '',
        imagesProcessed: shopImagesProcessed,
        sizechartsGenerated: shopSizechartsGenerated,
        bytesSavedMB: shopBytesSavedMB,
        templateUsage: shopTemplateUsage,
        creditsUsed: shopCreditsUsed
      });
    });
    
    // Calculate average compression ratio (if there are processed images)
    const averageCompressionRatio = totalImagesProcessed > 0 ? 
      // This is an approximation since we don't have original bytes data
      // A more accurate calculation would require original bytes data
      70 : 0;
    
    // Convert template usage by template to array and sort
    const byTemplateArray = Object.values(templateUsageByTemplate)
      .sort((a, b) => b.count - a.count);
    
    // Sort shop breakdown by images processed
    shopBreakdown.sort((a, b) => b.imagesProcessed - a.imagesProcessed);
    
    return {
      totalStats: {
        imagesProcessed: totalImagesProcessed,
        sizechartsGenerated: totalSizechartsGenerated,
        storageSaved: totalBytesSavedMB * 1024 * 1024, // Convert MB to bytes
        compressionRatio: averageCompressionRatio,
        creditsUsed: totalCreditsUsed
      },
      shopBreakdown,
      templateUsage: {
        total: totalTemplateUsage,
        successful: successfulTemplateUsage,
        failed: failedTemplateUsage,
        successRate: totalTemplateUsage > 0 ? 
          ((successfulTemplateUsage / totalTemplateUsage) * 100).toFixed(1) : '0.0',
        byTemplate: byTemplateArray
      },
      creditUsage: {
        total: totalCreditsUsed
      }
    };
  } catch (error) {
    console.error('Error fetching all shops metrics:', error);
    throw error;
  }
};
